// El expediente MDM del aparato de la venta abierta (`GET /sales/{saleId}/device`). Es una lectura
// APARTE: se dispara solo con una venta abierta, nunca por fila de la tabla.
import { useQuery } from '@tanstack/react-query';
import { wizardApi } from '@/api/services/retail-wizard';
import type { SaleDevice } from '@/api/services/retail-wizard/contract';

/**
 * Lo que la ficha sabe del candado del equipo. `error` es un estado propio: pintar una lectura
 * fallida como «sin candado» diría lo contrario de la verdad.
 */
export type SaleDeviceState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'error'; reason: string }
    | { status: 'ready'; data: SaleDevice };

export interface SaleDeviceResult {
    state: SaleDeviceState;
    /** Lo que dispara el «Reintentar» del cajón cuando la lectura falló. */
    refetch: () => void;
}

/** Resuelve el estado del cajón; sin venta o sin equipo no hay nada que leer. */
export function saleDeviceStateOf(
    saleId: string | null,
    data: SaleDevice | undefined,
    error: unknown,
    withEquipment = true,
): SaleDeviceState {
    if (saleId === null || !withEquipment) return { status: 'idle' };
    if (data) return { status: 'ready', data };
    if (error) return { status: 'error', reason: (error as Error).message };
    return { status: 'loading' };
}

/** El expediente del equipo de la venta abierta. Apagado sin venta o si la venta no trae teléfono. */
export function useSaleDevice(saleId: string | null, withEquipment = true): SaleDeviceResult {
    const query = useQuery({
        queryKey: ['retail-wizard', 'sale-device', saleId],
        queryFn: () => wizardApi.getSaleDevice(saleId ?? ''),
        enabled: saleId !== null && withEquipment,
        staleTime: 30_000,
    });

    return {
        state: saleDeviceStateOf(saleId, query.data, query.error, withEquipment),
        refetch: () => {
            void query.refetch();
        },
    };
}
